import { Badge } from "@tcg/ui";
import {
  CheckCircle2,
  PackageCheck,
  ScanLine,
  TriangleAlert,
  UserCheck,
  XCircle,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthState, useCan, usePos } from "@/app/context";
import { usePickups } from "@/realtime/pickups";
import { MoneyText } from "@/components/MoneyText";
import { useScanInput } from "@/features/sale/hooks/useScanInput";
import { call } from "@/lib/api";
import { errorText, isApiError, type ApiError } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";
import type { PickupFind, PickupOrder, PickupResult } from "@/lib/types";

import { FindByNumber } from "./FindByNumber";

import {
  emptyPickupPayment,
  PickupPay,
  pickupPaymentReady,
  pickupPayments,
  type PickupPayment,
} from "./PickupPay";
import { QrScanner } from "./QrScanner";
import { VerifyLines } from "./VerifyLines";

type Tab = "scan" | "number" | "today";

/** How the order was found — the hand-over sends the proof: the QR again, or (no QR) the
 * `lookupProof` pos-service answered to 「探す」 (its retries are not counted as new tries at
 * order-service — the number + contact go only once). */
type Proof =
  { kind: "qr"; code: string } | { kind: "lookup"; lookup: PickupFind; lookupProof: string | null };

/** A no-QR problem in words the cashier can act on (T07-42 review). */
function lookupProblem(err: unknown, t: (key: string) => string): string | null {
  if (isApiError(err, "rate_limited")) {
    // order-service's window is an hour — "a moment" would mislead
    return t("pickup.find_rate_limited");
  }
  if (isApiError(err, "pickup_not_found")) {
    const reason = (err as ApiError).detail.reason;
    return reason === "lookup_expired" ? t("pickup.find_expired") : t("pickup.find_not_found");
  }
  return null;
}

interface ScanNote {
  ok: boolean;
  text: string;
}

/**
 * 受取 (T07-29, design/06 §7–8): a web order picked up at the counter. Read the customer's QR
 * (the tablet camera, or the QR reader) → the order: name (masked), cards with Rank / grade,
 * paid or 店頭払い → scan every card before handing over (✓ per card; a wrong card ❌) →
 * take the 残額 (cash / card terminal) → 「お渡し完了」: order-service marks it picked up, the
 * 領収書 / 受渡票 prints. An order not ready yet says so and cannot be handed over.
 *
 * No QR (T07-42): 「番号で探す」 — the order number + the order's e-mail or phone (this shop's
 * orders only; order-service limits the tries); before handing over, the cashier confirms the
 * person is the one of the order (本人確認 — the QR is missing). 受取予定 is this shop's list and
 * updates itself (hub `pos:register:*`, T14-15); tapping a row opens 「番号で探す」 with its number.
 */
export function PickupScreen() {
  const { t } = useTranslation();
  const { api, printer } = usePos();
  const queryClient = useQueryClient();
  const unlocked = useAuthState().kind === "unlocked";
  const canList = useCan("orders.read");
  const [tab, setTab] = useState<Tab>("scan");
  const [order, setOrder] = useState<PickupOrder | null>(null);
  const [proof, setProof] = useState<Proof | null>(null);
  const [idChecked, setIdChecked] = useState(false);
  const [prefill, setPrefill] = useState({ number: "", round: 0 });
  const [verified, setVerified] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [note, setNote] = useState<ScanNote | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState<PickupPayment>(emptyPickupPayment);
  const [taxFree, setTaxFree] = useState(false);
  const [done, setDone] = useState<PickupResult | null>(null);
  const [printState, setPrintState] = useState<"idle" | "printed" | "failed">("idle");
  const key = useRef<string | null>(null);

  const reset = useCallback(() => {
    setOrder(null);
    setProof(null);
    setIdChecked(false);
    setVerified(new Map());
    setNote(null);
    setProblem(null);
    setPayment(emptyPickupPayment());
    setTaxFree(false);
    setDone(null);
    setPrintState("idle");
    key.current = null;
  }, []);

  const open = useCallback(
    async (next: Proof) => {
      setLooking(true);
      setProblem(null);
      setNote(null);
      try {
        const found = await call(() =>
          next.kind === "qr"
            ? api.GET("/v1/pos/pickups/lookup", { params: { query: { code: next.code } } })
            : api.POST("/v1/pos/pickups/find", { body: next.lookup }),
        );
        setOrder(found);
        setProof(
          next.kind === "lookup" ? { ...next, lookupProof: found.lookupProof ?? null } : next,
        );
        setIdChecked(false);
        setVerified(new Map());
        key.current = newIdempotencyKey();
      } catch (err) {
        setOrder(null);
        if (isApiError(err, "not_ready_for_pickup")) {
          const reason = (err as ApiError).detail.reason;
          setProblem(
            reason === "already_picked_up" ? t("pickup.already_picked_up") : t("pickup.not_ready"),
          );
        } else if (next.kind === "lookup" && lookupProblem(err, t)) {
          setProblem(lookupProblem(err, t));
        } else {
          setProblem(errorText(err, t));
        }
      } finally {
        setLooking(false);
      }
    },
    [api, t],
  );
  const lookup = useCallback((code: string) => open({ kind: "qr", code }), [open]);

  const verifyCard = useCallback(
    async (code: string) => {
      if (!order) {
        return;
      }
      try {
        const hit = await call(() => api.GET("/v1/pos/lookup", { params: { query: { code } } }));
        const unitId = hit.unit?.id ?? null;
        const line = order.lines.find((l) => l.needsScan && l.unitId && l.unitId === unitId);
        if (!line) {
          setNote({ ok: false, text: t("pickup.wrong_card", { code }) });
          return;
        }
        setVerified((current) => new Map(current).set(line.lineId, code));
        setNote({ ok: true, text: t("pickup.card_ok", { title: line.title }) });
      } catch {
        setNote({ ok: false, text: t("pickup.card_not_found", { code }) });
      }
    },
    [api, order, t],
  );

  const onCode = useCallback(
    (code: string) => {
      const value = code.trim();
      if (!value || done) {
        return;
      }
      if (!order) {
        void lookup(value);
      } else if (proof?.kind !== "qr" || value !== proof.code) {
        void verifyCard(value);
      }
    },
    [done, lookup, order, proof, verifyCard],
  );

  const scanInput = useScanInput({ onScan: onCode, onSearch: () => undefined, enabled: unlocked });

  // 受取予定: the layout keeps it live (a web order ready for pickup / handed over — T07-40, R21)
  const today = usePickups(canList);
  const waiting = today.data?.data.length ?? 0;

  const allVerified = order
    ? order.lines.every((l) => !l.needsScan || verified.has(l.lineId))
    : false;
  const due = order?.amountDue ?? 0;
  const payReady = pickupPaymentReady(payment, due);
  // no QR: the cashier confirmed the person against the order's name (本人確認)
  const idReady = proof?.kind !== "lookup" || idChecked;
  const canComplete = Boolean(order) && allVerified && payReady && idReady && !busy;

  const complete = async () => {
    if (!order || !proof || !canComplete) {
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const result = await call(() =>
        api.POST("/v1/pos/pickups/{order_id}/complete", {
          params: { path: { order_id: order.orderId } },
          body: {
            ...(proof.kind === "qr"
              ? { qrPayload: proof.code, verifiedBy: "qr" as const }
              : proof.lookupProof
                ? { lookupProof: proof.lookupProof, verifiedBy: "id_check" as const }
                : { lookup: proof.lookup, verifiedBy: "id_check" as const }),
            documentType: "kani_invoice",
            verifiedCodes: [...verified.values()],
            payments: pickupPayments(payment, due, null),
            taxFree,
            receiptOptions: { print: true, paperWidth: printer.config.paperWidth },
          },
          headers: { "Idempotency-Key": key.current ?? newIdempotencyKey() },
        }),
      );
      setDone(result);
      // the money is in this drawer now: レジ開閉 reads it again, the 受取予定 list loses the order
      void queryClient.invalidateQueries({ queryKey: ["pos-shifts"] });
      void queryClient.invalidateQueries({ queryKey: ["pos-pickups"] });
      if (result.openDrawer && printer.config.drawer) {
        await printer.queue.openDrawer();
      }
      if (result.slip.data) {
        const job = await printer.queue.submit({
          label: result.slip.receiptNo ?? result.orderNumber,
          receiptNo: result.slip.receiptNo ?? null,
          saleId: result.saleId,
          data: result.slip.data,
        });
        setPrintState(job.status === "done" ? "printed" : "failed");
      }
    } catch (err) {
      // a lost answer is retried with the same key (never collected twice); a refusal is final
      // for that key — the next try is a new request
      if (!isApiError(err, "network") && !(isApiError(err) && err.status >= 500)) {
        key.current = newIdempotencyKey();
      }
      if (isApiError(err, "pickup_item_mismatch")) {
        setVerified(new Map());
        setProblem(t("pickup.mismatch"));
      } else if (proof.kind === "lookup" && lookupProblem(err, t)) {
        setProblem(lookupProblem(err, t));
      } else {
        setProblem(errorText(err, t));
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section
        aria-labelledby="pickup-done"
        className="flex h-full flex-col items-center justify-center gap-5 p-8 text-center"
      >
        <h1 id="pickup-done" className="flex items-center gap-3 text-3xl font-semibold text-pos-ok">
          <CheckCircle2 aria-hidden className="size-10" />
          {t("pickup.done_title")}
        </h1>
        <p className="text-xl">{t("pickup.done_order", { no: done.orderNumber })}</p>
        {done.amountCollected > 0 ? (
          <div>
            <p className="text-muted-foreground">{t("done.change")}</p>
            <MoneyText amount={done.change} size="hero" data-testid="pickup-change" />
          </div>
        ) : null}
        <p aria-live="polite">
          {done.slip.kind === "pickup" ? t("pickup.slip_receipt") : t("pickup.slip_handover")}
          {" · "}
          {printState === "printed"
            ? t("done.printed")
            : printState === "failed"
              ? t("done.print_failed")
              : t("done.printing")}
        </p>
        <button
          type="button"
          autoFocus
          onClick={reset}
          className="min-h-24 w-80 rounded-lg bg-primary text-2xl font-bold text-primary-foreground"
        >
          {t("pickup.next")}
        </button>
      </section>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr] gap-3 p-4">
      <header className="flex items-center gap-3">
        <h1 className="flex-1 text-2xl font-semibold">{t("pickup.title")}</h1>
        <div role="tablist" aria-label={t("pickup.title")} className="flex gap-2">
          {(["scan", "number", "today"] as const).map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className="min-h-pos-touch rounded-md border px-5 text-lg aria-selected:border-primary aria-selected:bg-accent"
            >
              {t(`pickup.tab_${id}`)}
              {id === "today" && canList && waiting > 0 ? (
                <span className="ml-2 rounded-full bg-primary px-2 text-base text-primary-foreground">
                  {waiting}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </header>
      {tab === "today" ? (
        <section aria-label={t("pickup.tab_today")} className="flex min-h-0 flex-col gap-2">
          {!canList ? (
            <p className="text-muted-foreground">{t("pickup.list_not_allowed")}</p>
          ) : null}
          <p className="text-muted-foreground">{t("pickup.list_help")}</p>
          <ul className="flex min-h-0 flex-col gap-2 overflow-y-auto">
            {(today.data?.data ?? []).map((row) => (
              <li key={row.orderId}>
                {/* tap → 「番号で探す」 with the number (the customer still gives the e-mail / phone) */}
                <button
                  type="button"
                  onClick={() => {
                    reset();
                    setPrefill((p) => ({ number: row.orderNumber, round: p.round + 1 }));
                    setTab("number");
                  }}
                  className="grid min-h-pos-touch w-full grid-cols-[10rem_1fr_auto_auto] items-center gap-3 rounded-md border px-4 text-left hover:bg-accent"
                >
                  <span className="font-mono">{row.orderNumber}</span>
                  <span>{row.customerName ?? ""}</span>
                  {row.payAtStore ? (
                    <Badge variant="pending">{t("pickup.pay_at_store")}</Badge>
                  ) : (
                    <Badge variant="outline">{t("pickup.paid")}</Badge>
                  )}
                  {row.total !== null ? <MoneyText amount={row.total} /> : <span />}
                </button>
              </li>
            ))}
            {canList && today.data && today.data.data.length === 0 ? (
              <li className="text-muted-foreground">{t("pickup.list_empty")}</li>
            ) : null}
          </ul>
        </section>
      ) : (
        <div className="grid min-h-0 grid-cols-[22rem_minmax(0,1fr)] gap-4">
          <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto">
            {order || tab === "scan" ? (
              <label className="flex min-h-pos-touch items-center gap-2 rounded-md border px-3 focus-within:ring-2 focus-within:ring-ring">
                <ScanLine aria-hidden className="size-6 shrink-0" />
                <span className="sr-only">
                  {order ? t("pickup.scan_card_label") : t("pickup.scan_qr_label")}
                </span>
                <input
                  {...scanInput.inputProps}
                  autoComplete="off"
                  enterKeyHint="go"
                  placeholder={
                    order ? t("pickup.scan_card_placeholder") : t("pickup.scan_qr_placeholder")
                  }
                  className="min-w-0 flex-1 bg-transparent text-lg outline-none"
                />
              </label>
            ) : null}
            {!order && tab === "scan" ? <QrScanner onCode={onCode} /> : null}
            {!order && tab === "number" ? (
              canList ? (
                <FindByNumber
                  key={prefill.round}
                  initialNumber={prefill.number}
                  busy={looking}
                  onFind={(body) => void open({ kind: "lookup", lookup: body, lookupProof: null })}
                />
              ) : (
                <p className="text-muted-foreground">{t("pickup.find_not_allowed")}</p>
              )
            ) : null}
            {order ? (
              <button
                type="button"
                onClick={reset}
                className="min-h-pos-touch rounded-md border text-lg"
              >
                {t("pickup.other_order")}
              </button>
            ) : null}
            {looking ? <p aria-live="polite">{t("app.loading")}</p> : null}
            {problem ? (
              <p
                role="alert"
                data-testid="pickup-problem"
                className="flex items-start gap-2 rounded-md border-2 border-status-problem p-3 text-lg text-pos-problem"
              >
                <TriangleAlert aria-hidden className="size-6 shrink-0" />
                {problem}
              </p>
            ) : null}
          </aside>
          {order ? (
            <section
              aria-labelledby="pickup-order"
              className="flex min-h-0 flex-col gap-3 overflow-y-auto"
            >
              <header className="flex flex-wrap items-center gap-3">
                <h2 id="pickup-order" className="text-xl font-semibold">
                  {t("pickup.order", { no: order.orderNumber })}
                </h2>
                <span className="text-lg">{order.customerName ?? ""}</span>
                {order.payAtStore && due > 0 ? (
                  <Badge variant="pending">{t("pickup.pay_at_store")}</Badge>
                ) : (
                  <Badge variant="outline">{t("pickup.paid")}</Badge>
                )}
              </header>
              {note ? (
                <p
                  role={note.ok ? "status" : "alert"}
                  data-testid="scan-note"
                  className={
                    note.ok
                      ? "flex items-center gap-2 text-lg text-pos-ok"
                      : "flex items-center gap-2 text-lg text-pos-problem"
                  }
                >
                  {note.ok ? <CheckCircle2 aria-hidden /> : <XCircle aria-hidden />}
                  {note.text}
                </p>
              ) : null}
              <VerifyLines lines={order.lines} verified={verified} />
              {due > 0 ? (
                <PickupPay amountDue={due} payment={payment} onChange={setPayment} />
              ) : null}
              {order.taxFreeAvailable ? (
                <label className="flex min-h-pos-touch items-center gap-3 rounded-md border px-3 text-lg">
                  <input
                    type="checkbox"
                    checked={taxFree}
                    onChange={(e) => setTaxFree(e.target.checked)}
                    className="size-6"
                  />
                  {t("pickup.tax_free")}
                </label>
              ) : null}
              {proof?.kind === "lookup" ? (
                <label className="flex min-h-pos-touch items-center gap-3 rounded-md border-2 border-status-pending px-3 py-2 text-lg">
                  <input
                    type="checkbox"
                    checked={idChecked}
                    onChange={(e) => setIdChecked(e.target.checked)}
                    className="size-6 shrink-0"
                  />
                  <UserCheck aria-hidden className="size-6 shrink-0" />
                  {t("pickup.id_check", { name: order.customerName ?? "" })}
                </label>
              ) : null}
              {!allVerified ? (
                <p className="text-muted-foreground">{t("pickup.scan_all_first")}</p>
              ) : null}
              <button
                type="button"
                onClick={() => void complete()}
                disabled={!canComplete}
                className="flex min-h-20 items-center justify-center gap-2 rounded-lg bg-primary text-2xl font-bold text-primary-foreground disabled:opacity-40"
              >
                <PackageCheck aria-hidden className="size-8" />
                {busy
                  ? t("app.saving")
                  : due > 0
                    ? t("pickup.complete_with_pay")
                    : t("pickup.complete")}
              </button>
            </section>
          ) : (
            <section className="flex flex-col items-center justify-center gap-2 text-center text-muted-foreground">
              <p className="text-xl">
                {tab === "number" ? t("pickup.find_start_help") : t("pickup.start_help")}
              </p>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
